import * as React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import { TextField } from "../basic/TextField";
import debounce from "lodash.debounce";

export function CountrySelectField(props) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState([]);
  const [inputValue, setInputValue] = React.useState("");
  const [inputValueObj, setInputValueObj] = React.useState(props.cityObj);
  const [allCities, setAllCities] = React.useState([]);
  const fetchAbortController = React.useRef(null);

  // Fetch cities once when component mounts
  React.useEffect(() => {
    let active = true;

    const fetchCities = async () => {
      try {
        fetchAbortController.current?.abort(); // Cancel previous fetch
        const controller = new AbortController();
        fetchAbortController.current = controller;

        const response = await fetch(
          `${process.env.REACT_APP_WEBSITE}/cities500.json`,
          {
            signal: controller.signal,
          }
        );

        if (!response.ok) throw new Error("Failed to load cities");

        const cities = await response.json();
        if (active) {
          setAllCities(cities);
        }
      } catch (error) {
        if (error.name !== "AbortError") console.error(error);
      }
    };

    fetchCities();

    return () => {
      active = false;
      fetchAbortController.current?.abort(); // Cleanup on unmount
    };
  }, []);

  // Filter cities based on input (debounced)
  const debouncedFilter = React.useMemo(
    () =>
      debounce((query) => {
        if (!query) return setOptions([]);
        const filtered = allCities
          .filter((city) =>
            city.name.toLowerCase().includes(query.toLowerCase())
          )
          .slice(0, 20); // Limit results
        setOptions(filtered);
      }, 300), // 300ms debounce
    [allCities]
  );

  React.useEffect(() => {
    debouncedFilter(inputValue);
  }, [inputValue, debouncedFilter]);

  const handleSelection = (value, event) => {
    var newValue = event?.target?.attributes?.optionObj?.value
      ? JSON?.parse(event?.target?.attributes?.optionObj?.value)
      : null;

    setInputValue(value);

    if (typeof newValue === "object" && newValue !== null) {
      if (Object.keys(newValue)?.length > 0) {
        props.callback(newValue);
      }
    }
  };

  return (
    <Autocomplete
      sx={{
        width: "100%",
        "& input": {
          paddingTop: 1,
          paddingBottom: 1,
          bgcolor: "background.paper",
          color: (theme) =>
            theme.palette.getContrastText(theme.palette.background.paper),
        },
      }}
      id="custom-input-demo"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      isOptionEqualToValue={(option, value) => option.name === value.name}
      options={options}
      getOptionLabel={(option) => `${option.name} (${option.country})`}
      onInputChange={(_, value) => handleSelection(value, _)}
      defaultValue={inputValueObj}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box
            key={key}
            optionObj={JSON.stringify(option)}
            component="li"
            sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
            {...optionProps}
          >
            {/* <img
              loading="lazy"
              width="20"
              srcSet={`https://flagcdn.com/w40/${option.country.toLowerCase()}.png 2x`}
              src={`https://flagcdn.com/w20/${option.country.toLowerCase()}.png`}
              alt=""
            /> */}
            {option.name} ({option.country})
          </Box>
        );
      }}
      renderInput={(params) => (
        <div ref={params.InputProps.ref}>
          <TextField
            fullWidth={true}
            label="Set your location"
            {...params.inputProps}
          />
        </div>
      )}
    />
  );
}

export default CountrySelectField;
